import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { ChatUserType } from '@/signalr';
import type { ChatMessage } from '@/signalr';
import { EmptyDataMessage } from '@/ui';
import { manageScrolling } from '@/lib/chat/chatScrolling';
import { openMediaViewerDialog } from '@/features/incident-detail/actions';
import { CallerImagePreviewComponent } from './CallerImagePreviewComponent';
import { CallerMessageComponent } from './CallerMessageComponent';
import { CallerMessagePreviewComponent } from './CallerMessagePreviewComponent';
import { DispatcherMessageComponent } from './DispatcherMessageComponent';

export type ChatMessageListProps = {
  messages: ChatMessage[];
  callerMessagePreview: string | null;
  isCallerSendingImage: boolean;
  translateChat: boolean;
};

export function ChatMessageList({
  messages,
  callerMessagePreview,
  isCallerSendingImage,
  translateChat,
}: ChatMessageListProps) {
  const { t } = useTranslation();

  useEffect(() => manageScrolling('chat-message-list'), []);

  const showZoomInDialog = (url: string) => {
    openMediaViewerDialog([{ url, mimeType: 'image/jpeg', uploadedAt: new Date().toISOString() }], 0);
  };

  return (
    <div className="chat-message-list d-flex flex-column gap-1 pt-3">
      {messages.length > 0 ? (
        messages.map((message) =>
          message.userType === ChatUserType.Caller ? (
            <CallerMessageComponent
              key={message.id}
              message={message}
              translateChat={translateChat}
              onImageClick={showZoomInDialog}
            />
          ) : message.userType === ChatUserType.Dispatcher ? (
            <DispatcherMessageComponent key={message.id} message={message} onImageClick={showZoomInDialog} />
          ) : (
            <div key={message.id}>Unknown message type</div>
          ),
        )
      ) : (
        <EmptyDataMessage fullHeight text={t('Chat_NoMessagesNote')} />
      )}

      <CallerImagePreviewComponent isSending={isCallerSendingImage} />
      <CallerMessagePreviewComponent messagePreview={callerMessagePreview} translateChat={translateChat} />
    </div>
  );
}
